// subscriptionRepository.ts
// DEPRECATED: Use PrivateProfileService.updateProfile() instead
// This repository bypasses the service layer - all profile writes should go through PrivateProfileService
//
// TODO: Refactor subscriptionService to use PrivateProfileService directly

import { PrivateProfileService } from '../services/privateProfileService';
import { SubscriptionTier } from '../models/PrivateProfile';
import { errorReporting } from '../services/errorReportingService';

export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface SubscriptionUpdate {
  subscriptionTier: SubscriptionTier;
  subscriptionExpiresAt: number | null;
  lastRevenueCatSync: number;
  revenueCatCustomerId?: string;
}

/**
 * SubscriptionRepository
 * Delegates to PrivateProfileService (single source of truth)
 */
export class SubscriptionRepository {
  /**
   * Update user's subscription information via PrivateProfileService
   * Uses the proper service layer instead of direct Firestore access
   */
  static async updateSubscription(
    userId: string,
    subscriptionData: SubscriptionUpdate
  ): Promise<ServiceResponse<boolean>> {
    try {
      // Use PrivateProfileService as single source of truth
      const result = await PrivateProfileService.updateProfile(userId, {
        subscriptionTier: subscriptionData.subscriptionTier,
        subscriptionExpiresAt: subscriptionData.subscriptionExpiresAt,
        lastRevenueCatSync: subscriptionData.lastRevenueCatSync,
        revenueCatCustomerId: subscriptionData.revenueCatCustomerId,
      });

      if (result.success) {
        console.log('[SubscriptionRepository] Subscription updated via PrivateProfileService:', userId, subscriptionData.subscriptionTier);
        return { success: true, data: true };
      }
      
      return { success: false, error: result.error };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errorReporting.reportError(error, {
        screen: 'SubscriptionRepository',
        action: 'updateSubscription',
        userId,
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get user's subscription information via PrivateProfileService
   */
  static async getSubscription(userId: string): Promise<ServiceResponse<SubscriptionUpdate>> {
    try {
      const result = await PrivateProfileService.getProfile(userId);
      
      if (!result.success || !result.data) {
        return { success: false, error: result.error || 'Profile not found' };
      }

      const profile = result.data;
      const subscription: SubscriptionUpdate = {
        subscriptionTier: profile.subscriptionTier || 'free',
        subscriptionExpiresAt: profile.subscriptionExpiresAt ?? null,
        lastRevenueCatSync: profile.lastRevenueCatSync || 0,
        revenueCatCustomerId: profile.revenueCatCustomerId,
      };

      return { success: true, data: subscription };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errorReporting.reportError(error, {
        screen: 'SubscriptionRepository',
        action: 'getSubscription',
        userId,
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Check if cached subscription data is stale (older than threshold)
   */
  static isSubscriptionStale(lastSyncTime: number, thresholdMinutes = 5): boolean {
    const now = Math.floor(Date.now() / 1000);
    const thresholdSeconds = thresholdMinutes * 60;
    return (now - lastSyncTime) > thresholdSeconds;
  }

  /**
   * Migrate existing user from old subscription model
   * Sets users without new tier field to 'free' tier
   */
  static async migrateUserSubscription(userId: string): Promise<ServiceResponse<boolean>> {
    try {
      const profileResult = await PrivateProfileService.getProfile(userId);
      
      if (!profileResult.success || !profileResult.data) {
        return { success: false, error: 'User profile not found' };
      }

      const profile = profileResult.data;

      // Check if already migrated (has subscriptionTier field)
      if (profile.subscriptionTier && profile.subscriptionTier !== 'free') {
        return { success: true, data: true }; // Already migrated
      }

      // Migrate to free tier by default via PrivateProfileService
      const result = await PrivateProfileService.updateProfile(userId, {
        subscriptionTier: 'free',
        subscriptionExpiresAt: null,
        lastRevenueCatSync: Math.floor(Date.now() / 1000),
      });

      if (result.success) {
        console.log('[SubscriptionRepository] User migrated to new subscription model:', userId);
        return { success: true, data: true };
      }
      
      return { success: false, error: result.error };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errorReporting.reportError(error, {
        screen: 'SubscriptionRepository',
        action: 'migrateUserSubscription',
        userId,
      });
      return { success: false, error: errorMessage };
    }
  }
}

